/**
 * Token Storage Utility - Safari-resilient dual storage
 *
 * Writes to both localStorage AND sessionStorage.
 * Reads from localStorage first, falls back to sessionStorage.
 * If localStorage was purged (Safari ITP / memory pressure),
 * auto-restores from sessionStorage.
 *
 * sessionStorage is per-tab and NOT affected by Safari ITP,
 * so it survives mid-session even when iOS purges localStorage.
 */

const PREFIX = '__ak_'; // namespace to avoid collisions on restore

// Safely try a storage operation — returns undefined on failure
const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return undefined;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage full or unavailable — silently ignore
  }
};

const safeRemove = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore
  }
};

/**
 * Get a value — tries localStorage, falls back to sessionStorage.
 * If localStorage was purged but sessionStorage has the value, auto-restores.
 */
export const getItem = (key) => {
  const fromLocal = safeGet(localStorage, key);
  if (fromLocal !== undefined && fromLocal !== null) {
    return fromLocal;
  }

  // localStorage empty — try sessionStorage backup
  const fromSession = safeGet(sessionStorage, PREFIX + key);
  if (fromSession !== undefined && fromSession !== null) {
    // Auto-restore to localStorage
    safeSet(localStorage, key, fromSession);
    console.log(`[TokenStorage] Restored "${key}" from sessionStorage backup`);
    return fromSession;
  }

  return null;
};

/**
 * Set a value — writes to both storages.
 */
export const setItem = (key, value) => {
  safeSet(localStorage, key, value);
  safeSet(sessionStorage, PREFIX + key, value);
};

/**
 * Remove a value — removes from both storages.
 */
export const removeItem = (key) => {
  safeRemove(localStorage, key);
  safeRemove(sessionStorage, PREFIX + key);
};

// --- Convenience methods for auth tokens ---

export const getToken = () => getItem('token');
export const setToken = (token) => setItem('token', token);
export const removeToken = () => removeItem('token');

export const getUser = () => {
  const raw = getItem('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
export const setUser = (user) => setItem('user', JSON.stringify(user));
export const removeUser = () => removeItem('user');

// --- Convenience methods for participant data ---

export const getParticipantData = (sessionId) => {
  // Try session-specific key first
  if (sessionId) {
    const sessionSpecific = getItem(`participant_${sessionId}`);
    if (sessionSpecific) {
      try {
        return JSON.parse(sessionSpecific);
      } catch { /* fall through */ }
    }
  }

  // Fall back to general keys
  const current = getItem('current_participant');
  if (current) {
    try {
      return JSON.parse(current);
    } catch { /* fall through */ }
  }

  const general = getItem('participantData');
  if (general) {
    try {
      return JSON.parse(general);
    } catch { /* fall through */ }
  }

  return null;
};

export const setParticipantData = (sessionId, data) => {
  const json = JSON.stringify(data);
  if (sessionId) {
    setItem(`participant_${sessionId}`, json);
  }
  setItem('current_participant', json);
  setItem('participantData', json);
};

export const removeParticipantData = (sessionId) => {
  if (sessionId) {
    removeItem(`participant_${sessionId}`);
    removeParticipantToken(sessionId);
  }
  removeItem('current_participant');
  removeItem('participantData');
};

// --- Signed participant identity token (see backend/src/utils/participantToken.js) ---
//
// Kept under its OWN key rather than inside the participant data object: several
// recovery paths rewrite participant data wholesale from a server response that
// (deliberately) carries no token, and would otherwise wipe it.

const participantTokenKey = (sessionId) => `participant_token_${sessionId}`;

export const getParticipantToken = (sessionId) =>
  (sessionId ? getItem(participantTokenKey(sessionId)) : null);

export const setParticipantToken = (sessionId, token) => {
  if (sessionId && token) setItem(participantTokenKey(sessionId), token);
};

export const removeParticipantToken = (sessionId) => {
  if (sessionId) removeItem(participantTokenKey(sessionId));
};

/**
 * Store the token from a join / rejoin / recover response, if the server issued one.
 * A null token (returning participant matched by name/email/cookie) leaves any token
 * already stored for that session untouched.
 */
export const saveParticipantTokenFrom = (data) => {
  if (data?.participantToken && data?.sessionId) {
    setParticipantToken(String(data.sessionId), data.participantToken);
  }
};

// Wipe everything tied to a single session — used when a participant joins
// as "someone else" on a device that has prior identity/responses cached.
// Without this, the new participant lands on the active-session page and
// sees the previous person's "already responded" flags, drafts, and saved
// responses (all keyed by sessionId, not by participantId).
export const clearSessionData = (sessionId) => {
  if (!sessionId) return;

  // Targeted keys we know are session-scoped. Listed explicitly (rather than
  // a startsWith() sweep) so this stays predictable and doesn't collide with
  // unrelated app state that happens to embed sessionId in its key.
  const exactKeys = [
    `participant_${sessionId}`,
    `participant_view_${sessionId}`,
    `pending_emits_${sessionId}`,
    'current_participant',
    'participantData',
  ];
  exactKeys.forEach((k) => {
    try { localStorage.removeItem(k); } catch { /* ignore */ }
    try { sessionStorage.removeItem(k); } catch { /* ignore */ }
  });

  // The previous person's signed identity must not survive "join as someone else".
  // removeItem clears both storages, including the prefixed sessionStorage backup
  // that getItem would otherwise auto-restore from.
  removeParticipantToken(sessionId);

  // Activity-keyed entries — written by every submit handler. We don't know
  // the activity IDs up front, so iterate keys and match the patterns.
  const sessionScopedPrefixes = [
    `responded_${sessionId}_`,
    `poll_response_${sessionId}_`,
    `wordcloud_${sessionId}_`,
    `feedback_response_${sessionId}_`,
    `challenge_response_${sessionId}_`,
    `actionplan_response_${sessionId}_`,
    `openquestion_response_${sessionId}_`,
    `pending_challenges_${sessionId}_`,
    `pending_actionplans_${sessionId}_`,
  ];

  const sweepStorage = (storage) => {
    const toRemove = [];
    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && sessionScopedPrefixes.some((p) => key.startsWith(p))) {
          toRemove.push(key);
        }
      }
    } catch { /* ignore */ }
    toRemove.forEach((k) => {
      try { storage.removeItem(k); } catch { /* ignore */ }
    });
  };
  sweepStorage(localStorage);
  sweepStorage(sessionStorage);
};

// --- Bulk cleanup (for logout) ---

export const clearAll = () => {
  // Remove auth
  removeToken();
  removeUser();
  removeItem('current_participant');
  removeItem('participantData');

  // Remove all session-specific and participant keys from both storages
  const keysToRemove = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('participant_') ||
        key.startsWith('training_session_') ||
        key.startsWith('welcome_shown_')
      )) {
        keysToRemove.push(key);
      }
    }
  } catch { /* ignore */ }

  keysToRemove.forEach(key => removeItem(key));

  // Clear sessionStorage completely
  try {
    sessionStorage.clear();
  } catch { /* ignore */ }
};

export default {
  getItem,
  setItem,
  removeItem,
  getToken,
  setToken,
  removeToken,
  getUser,
  setUser,
  removeUser,
  getParticipantData,
  setParticipantData,
  removeParticipantData,
  getParticipantToken,
  setParticipantToken,
  removeParticipantToken,
  saveParticipantTokenFrom,
  clearSessionData,
  clearAll,
};
